import React from "react";
import { Link } from "react-router-dom";
import {
 ArrowUpRight, Sprout, Droplet, Waves, Flame, Nut, Leaf, Wheat,
 Package, Truck, Handshake, ShieldCheck, Check, ChevronDown
} from "lucide-react";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import { useT } from "@/i18n/helpers";
import Seo from "@/components/Seo";

const Section = ({ children, className = "", id }) => (
 <section id={id} className={`border-t border-nanda-line ${className}`}>
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-28">{children}</div>
 </section>
);
const Overline = ({ children }) => (<div className="label-overline text-nanda-muted mb-4">{children}</div>);

export default function MatieresPremieresNaturelles() {
 const tt = useT();

 const FAMILLES = [
 { i: Nut, t: tt("Anacarde (noix et amande de cajou)", "Cashew (raw and kernel)", "Caju (castanha e amêndoa)"),
 d: tt("Sourcing en Guinée-Bissau : noix brute (RCN), amandes transformées, dérivés.",
 "Sourcing in Guinea-Bissau: raw nuts (RCN), processed kernels, by-products.",
 "Sourcing na Guiné-Bissau: castanha em bruto (RCN), amêndoas transformadas, derivados.") },
 { i: Sprout, t: tt("Fruits tropicaux", "Tropical fruits", "Frutos tropicais"),
 d: tt("Mangue, papaye, banane, agrumes, ananas, karkadé. Frais, séchés, en purée ou surgelés.",
 "Mango, papaya, banana, citrus, pineapple, karkadé. Fresh, dried, puréed or frozen.",
 "Manga, papaia, banana, citrinos, ananás, karkadé. Frescos, secos, em polpa ou congelados.") },
 { i: Droplet, t: tt("Miels & produits de la ruche", "Honey & bee products", "Mel e produtos da colmeia"),
 d: tt("Miels de mangrove, de savane et de forêt. Traçabilité par apiculteur et par saison.",
 "Mangrove, savannah and forest honeys. Traceability by beekeeper and season.",
 "Méis de mangue, de savana e de floresta. Rastreabilidade por apicultor e por estação.") },
 { i: Waves, t: tt("Sel naturel & fleur de sel", "Natural salt & fleur de sel", "Sal natural e flor de sal"),
 d: tt("Récoltes solaires artisanales de Guinée-Bissau, cristaux purs et fleur de sel d'exception.",
 "Artisanal solar harvests from Guinée-Bissau, pure crystals and exceptional fleur de sel.",
 "Colheitas solares artesanais da Guiné-Bissau, cristais puros e flor de sal de excepção.") },
 { i: Flame, t: tt("Épices & aromates", "Spices & aromatics", "Especiarias e aromáticas"),
 d: tt("Poivre de Sélim, gingembre, curcuma, piments, karkadé. Séchage soigné, calibrage professionnel.",
 "Selim pepper, ginger, turmeric, chillies, karkadé. Careful drying, professional grading.",
 "Pimenta de Selim, gengibre, curcuma, malaguetas, karkadé. Secagem cuidada, calibragem profissional.") },
 { i: Leaf, t: tt("Plantes médicinales & bien-être", "Medicinal & wellness plants", "Plantas medicinais e bem-estar"),
 d: tt("Kinkeliba, baobab, moringa, hibiscus, neem. Feuilles, poudres, extraits.",
 "Kinkeliba, baobab, moringa, hibiscus, neem. Leaves, powders, extracts.",
 "Kinkeliba, baobá, moringa, hibisco, neem. Folhas, pós, extractos.") },
 { i: Wheat, t: tt("Céréales anciennes", "Ancient grains", "Cereais antigos"),
 d: tt("Fonio, mil, sorgho, riz local. Post-récolte, décorticage, précuisson.",
 "Fonio, millet, sorghum, local rice. Post-harvest, hulling, pre-cooking.",
 "Fonio, milho-miúdo, sorgo, arroz local. Pós-colheita, descasque, pré-cozedura.") },
 { i: Package, t: tt("Ingrédients cosmétiques", "Cosmetic ingredients", "Ingredientes cosméticos"),
 d: tt("Beurre de karité, huile de baobab, huile de touloucouna, huile de moringa. Extractions traditionnelles.",
 "Shea butter, baobab oil, touloucouna oil, moringa oil. Traditional extractions.",
 "Manteiga de karité, óleo de baobá, óleo de toulucuna, óleo de moringa. Extracções tradicionais.") },
 ];

 const SERVICES = [
 { i: Handshake, t: tt("Sourcing & mise en relation", "Sourcing & matchmaking", "Sourcing e mise en relation"),
 d: tt("Identification de producteurs, coopératives et transformateurs sérieux, ancrés localement.",
 "Identification of serious, locally rooted producers, cooperatives and processors.",
 "Identificação de produtores, cooperativas e transformadores sérios, enraizados localmente.") },
 { i: ShieldCheck, t: tt("Contrôle qualité & traçabilité", "Quality control & traceability", "Controlo de qualidade e rastreabilidade"),
 d: tt("Suivi de la matière du champ au conditionnement : origine, saison, lot, préparation.",
 "Material tracking from field to packaging: origin, season, batch, preparation.",
 "Acompanhamento da matéria do campo à embalagem: origem, estação, lote, preparação.") },
 { i: Package, t: tt("Conditionnement & valorisation", "Packaging & valorisation", "Embalagem e valorização"),
 d: tt("Nettoyage, séchage, calibrage, emballage. Cahiers des charges adaptés au client.",
 "Cleaning, drying, grading, packaging. Specifications adapted to the client.",
 "Limpeza, secagem, calibragem, embalagem. Cadernos de encargos adaptados ao cliente.") },
 { i: Truck, t: tt("Logistique & export", "Logistics & export", "Logística e exportação"),
 d: tt("Groupage, documents douaniers, coordination avec les port autonome de Bissau.",
 "Consolidation, customs documents, coordination with the port of Bissau.",
 "Consolidação, documentos aduaneiros, coordenação com os porto autónomo de Bissau.") },
 ];

 const CLIENTS = [
 tt("Importateurs & distributeurs alimentaires", "Food importers & distributors", "Importadores e distribuidores alimentares"),
 tt("Marques d'épicerie fine & de bien-être", "Fine grocery & wellness brands", "Marcas de mercearia fina e bem-estar"),
 tt("Industriels agroalimentaires", "Food industry manufacturers", "Industriais agro-alimentares"),
 tt("Fabricants cosmétiques & pharmaceutiques", "Cosmetics & pharmaceutical manufacturers", "Fabricantes cosméticos e farmacêuticos"),
 tt("Restaurateurs et chefs curieux", "Curious restaurateurs and chefs", "Restauradores e chefs curiosos"),
 tt("Retailers spécialisés Afrique", "Africa-focused specialised retailers", "Retalhistas especializados em África"),
 ];

 const PRINCIPES = [
 tt("Prix justes payés aux producteurs.", "Fair prices paid to producers.", "Preços justos pagos aos produtores."),
 tt("Volumes réalistes, pas d'engagement que nous ne pouvons tenir.", "Realistic volumes, no commitment we cannot honour.", "Volumes realistas, nenhum compromisso que não possamos cumprir."),
 tt("Traçabilité et honnêteté sur l'origine et la saison.", "Traceability and honesty about origin and season.", "Rastreabilidade e honestidade sobre a origem e a estação."),
 tt("Respect des savoir-faire traditionnels.", "Respect for traditional know-how.", "Respeito pelos saberes tradicionais."),
 tt("Aucune promesse fantaisiste : nous n'exportons que ce que nous maîtrisons.", "No fanciful promises: we only export what we master.", "Nenhuma promessa fantasiosa: só exportamos o que dominamos."),
 ];

 return (
 <div data-testid="page-matieres-premieres">
 <Seo />
 <section className="relative h-[92vh] min-h-[600px] w-full overflow-hidden bg-nanda-ink">
 <img src="/photos/nanda-sa-food-hero.jpg" alt={tt("Matières premières naturelles d'Afrique de l'Ouest", "West African natural raw materials", "Matérias-primas naturais da África Ocidental")} className="absolute inset-0 h-full w-full object-cover opacity-70" loading="eager" />
 <div className="absolute inset-0 bg-gradient-to-b from-nanda-ink/50 via-nanda-ink/25 to-nanda-ink/80" />
 <div className="relative z-10 h-full mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 flex flex-col justify-end pb-24 md:pb-32">
 <FadeUp>
 <div className="label-overline text-white/85 mb-6">{tt("Sourcing & valorisation ouest-africaine", "West African sourcing & valorisation", "Sourcing e valorização oeste-africana")}</div>
 <h1 className="font-serif text-white text-5xl md:text-7xl lg:text-[88px] leading-[1.02] max-w-5xl">
 {tt("Matières premières", "Natural raw materials", "Matérias-primas")}<br />
 <span className="text-nanda-gold italic">{tt("naturelles d'Afrique de l'Ouest.", "of West Africa.", "naturais da África Ocidental.")}</span>
 </h1>
 <p className="mt-8 max-w-2xl text-white/85 text-lg md:text-xl leading-relaxed">
 {tt("Sourcing, contrôle qualité, valorisation et mise en relation autour des matières agricoles, alimentaires et botaniques d'Afrique de l'Ouest, avec un ancrage en Guinée-Bissau.",
 "Sourcing, quality control, valorisation and matchmaking around agricultural, food and botanical raw materials from West Africa, anchored in Guinea-Bissau.",
 "Sourcing, controlo de qualidade, valorização e mise en relation em matérias agrícolas, alimentares e botânicas da África Ocidental, com enraizamento na Guiné-Bissau.")}
 </p>
 <div className="mt-12 flex flex-wrap gap-4">
 <Link to="/contact?subject=Matieres-premieres" className="inline-flex items-center gap-3 bg-white text-nanda-ink px-8 py-4 label-overline hover:bg-nanda-gold hover:text-white transition-colors duration-500" data-testid="mp-cta-contact">
 {tt("Discuter d'un sourcing", "Discuss a sourcing project", "Discutir um sourcing")} <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 <a href="#familles" className="inline-flex items-center gap-3 border border-white/50 text-white px-8 py-4 label-overline hover:bg-white hover:text-nanda-ink transition-colors duration-500" data-testid="mp-cta-familles">
 {tt("Voir les familles", "See the families", "Ver as famílias")} <ChevronDown size={16} strokeWidth={1.5} />
 </a>
 </div>
 </FadeUp>
 </div>
 </section>

 <Section>
 <div className="grid grid-cols-1 md:grid-cols-12 gap-14">
 <FadeUp className="md:col-span-5">
 <Overline>{tt("Notre positionnement", "Our positioning", "O nosso posicionamento")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Des matières que l'on connaît, un à un.", "Materials we know, one by one.", "Matérias que conhecemos, uma a uma.")}
 </h2>
 </FadeUp>
 <FadeUp delay={0.15} className="md:col-span-7 text-nanda-inkSoft text-[17px] leading-relaxed space-y-5">
 <p>{tt("L'Afrique de l'Ouest concentre certaines des matières premières naturelles les plus recherchées au monde : anacarde, mangue, baobab, karité, fleur de sel, miel de mangrove, hibiscus, fonio. Beaucoup partent encore sans transformation, sans traçabilité, sans reconnaissance.",
 "West Africa is home to some of the most sought-after natural raw materials in the world: cashew, mango, baobab, shea, fleur de sel, mangrove honey, hibiscus, fonio. Many still leave without processing, traceability or recognition.",
 "A África Ocidental concentra algumas das matérias-primas naturais mais procuradas do mundo: caju, manga, baobá, karité, flor de sal, mel de mangue, hibisco, fonio. Muitas partem ainda sem transformação, sem rastreabilidade, sem reconhecimento.")}</p>
 <p>{tt("NANDA SÁ travaille à changer cela, à sa mesure : identifier les producteurs qui font bien, structurer la qualité en amont, ajouter de la valeur localement lorsque c'est possible, et connecter cette matière à des acheteurs sérieux, en Afrique et à l'international.",
 "NANDA SÁ works to change this, at its own scale: identifying producers who do it right, structuring quality upstream, adding value locally when possible, and connecting these materials to serious buyers in Africa and internationally.",
 "A NANDA SÁ trabalha para mudar isto, à sua medida: identificar produtores que fazem bem, estruturar a qualidade a montante, acrescentar valor localmente quando possível, e ligar essa matéria a compradores sérios, em África e internacionalmente.")}</p>
 <p>{tt("Nous ne prétendons pas exporter des volumes que nous ne maîtrisons pas. Nous travaillons projet par projet, matière par matière, avec des partenaires qui partagent la même exigence.",
 "We do not claim to export volumes we cannot master. We work project by project, material by material, with partners who share the same standard.",
 "Não pretendemos exportar volumes que não dominamos. Trabalhamos projecto a projecto, matéria a matéria, com parceiros que partilham a mesma exigência.")}</p>
 </FadeUp>
 </div>
 </Section>

 <Section id="familles" className="bg-nanda-bgAlt">
 <FadeUp className="mb-14 max-w-3xl">
 <Overline>{tt("Nos familles de matières", "Our material families", "As nossas famílias de matérias")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Huit familles, un même sérieux.", "Eight families, one same seriousness.", "Oito famílias, uma mesma seriedade.")}
 </h2>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-nanda-line border border-nanda-line">
 {FAMILLES.map((f) => (
 <StaggerItem key={f.t} className="bg-white p-8 md:p-10">
 <f.i size={28} strokeWidth={1.2} className="text-nanda-terracotta mb-5" />
 <div className="font-serif text-xl md:text-2xl text-nanda-ink leading-tight">{f.t}</div>
 <p className="mt-4 text-sm text-nanda-inkSoft leading-relaxed">{f.d}</p>
 </StaggerItem>
 ))}
 </Stagger>
 </Section>

 <Section id="services">
 <FadeUp className="mb-14 max-w-3xl">
 <Overline>{tt("Nos services", "Our services", "Os nossos serviços")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Quatre services, du champ au client.", "Four services, from field to customer.", "Quatro serviços, do campo ao cliente.")}
 </h2>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-nanda-line border border-nanda-line">
 {SERVICES.map((s) => (
 <StaggerItem key={s.t} className="bg-white p-8 md:p-10">
 <s.i size={26} strokeWidth={1.2} className="text-nanda-ink mb-4" />
 <div className="font-serif text-xl text-nanda-ink leading-tight">{s.t}</div>
 <p className="mt-3 text-sm text-nanda-inkSoft leading-relaxed">{s.d}</p>
 </StaggerItem>
 ))}
 </Stagger>
 </Section>

 <Section className="bg-nanda-bgAlt">
 <FadeUp className="mb-12 max-w-3xl">
 <Overline>{tt("À qui nous parlons", "Who we talk to", "A quem nos dirigimos")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Six profils d'acheteurs.", "Six buyer profiles.", "Seis perfis de compradores.")}
 </h2>
 </FadeUp>
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-nanda-line border border-nanda-line">
 {CLIENTS.map((c) => (<div key={c} className="bg-white p-6 md:p-8"><div className="font-serif text-lg md:text-xl text-nanda-ink">{c}</div></div>))}
 </div>
 </Section>

 <Section>
 <FadeUp className="mb-12 max-w-3xl">
 <Overline>{tt("Nos principes", "Our principles", "Os nossos princípios")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Cinq règles simples.", "Five simple rules.", "Cinco regras simples.")}
 </h2>
 </FadeUp>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4">
 {PRINCIPES.map((p) => (
 <div key={p} className="flex items-start gap-3 py-4 border-t border-nanda-line">
 <Check size={18} strokeWidth={2} className="text-nanda-terracotta mt-1 shrink-0" />
 <span className="text-[15px] text-nanda-ink leading-snug">{p}</span>
 </div>
 ))}
 </div>
 </Section>

 <Section className="bg-nanda-ink text-white">
 <FadeUp className="max-w-4xl mx-auto text-center">
 <Overline><span className="text-nanda-gold">{tt("Discutons de votre projet", "Let's discuss your project", "Falemos do seu projecto")}</span></Overline>
 <h3 className="font-serif text-3xl md:text-5xl lg:text-6xl leading-[1.08] mt-4">
 {tt("Un besoin d'une matière première", "A need for a raw material", "Uma necessidade de uma matéria-prima")}<br />
 <span className="text-nanda-gold italic">{tt("ouest-africaine ?", "from West Africa?", "oeste-africana?")}</span>
 </h3>
 <p className="mt-8 text-white/80 text-lg leading-relaxed max-w-2xl mx-auto">
 {tt("Décrivez ce que vous cherchez : matière, volume estimé, qualité recherchée. Nous vous répondons personnellement.",
 "Describe what you're looking for: material, estimated volume, target quality. We reply personally.",
 "Descreva o que procura: matéria, volume estimado, qualidade desejada. Respondemos-lhe pessoalmente.")}
 </p>
 <div className="mt-12">
 <Link to="/contact?subject=Matieres-premieres-naturelles" className="inline-flex items-center gap-3 bg-nanda-gold text-white px-10 py-5 label-overline hover:bg-white hover:text-nanda-ink transition-colors duration-500" data-testid="mp-cta-final">
 {tt("Nous écrire", "Write to us", "Escreva-nos")} <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 </div>
 </FadeUp>
 </Section>
 </div>
 );
}
